import { useMemo, useState, useEffect } from 'react';
import { Box, Text, useDisclosure } from '@chakra-ui/react';
import {
    ColumnDef,
    SortingFn,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { IInternationalCompanyGroup, IOptionalInternationalCompanyGroup } from 'types';
import { linkStyle } from 'shared/styles';
import {
    useLazyGetInternationalCompanyGroupQuery,
    useGetInternationalCompanyGroupsQuery,
    useLazyGetInternationalCompanyDataQuery,
    useGetAllInternationalCompaniesQuery,
} from 'core/API/internationalCompanyGroupApi';

const emptyModalData = {
    users: [],
    available_users: [],
    company_groups: [],
    available_company_groups: [],
};

const useInternationalCompanyGroup = () => {
    const [languages, setLanguages] = useState<string[]>([]);
    const [filters, setFilters] = useState<{ id: string; value: unknown }[]>([]);
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();
    const [modalType, setModalType] = useState<'add' | 'update'>('add');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const translate = useTranslation();
    const [globalCompanyId, setGlobalCompanyId] = useState<number | null>(null);
    const { data: internationalCompanyGroupsData, isError, isLoading } = useGetInternationalCompanyGroupsQuery();
    const {
        data: internationalCompanies,
        isError: isInternationalCompaniesError,
        isLoading: isInternationalCompaniesLoading,
    } = useGetAllInternationalCompaniesQuery();
    const [
        getInternationalCompanyGroup,
        { data: internationalCompanyGroupData, isLoading: isInternationalCompanyGroupLoading },
    ] = useLazyGetInternationalCompanyGroupQuery();
    const [
        getInternationalCompanyGroupData,
        { data: internationalCompanyGroupDataData, isLoading: isInternationalCompanyGroupLoadingData },
    ] = useLazyGetInternationalCompanyDataQuery();
    const [modalData, setModalData] = useState<IOptionalInternationalCompanyGroup>(emptyModalData);
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const companyInfo = useGetCompanyInfoSelector();
    const userInfo = useGetUserInfoSelector();

    const data: Readonly<IInternationalCompanyGroup[]> = useMemo(() => {
        if (!isLoading && !isError && internationalCompanyGroupsData) {
            setGlobalCompanyId(internationalCompanyGroupsData.globalCompanyId);
            return internationalCompanyGroupsData.data;
        }
        return [];
    }, [isLoading, isError, internationalCompanyGroupsData]);

    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
        }
    }, [retailers, isRetailersLoading]);

    const caseInsensitiveSort = useMemo(() => SORTING_FNS.caseInsensitive, []);
    const sortTypes: Record<string, SortingFn<any>> = useMemo(
        () => ({
            ...SORTING_FNS,
        }),
        [],
    );

    const defaultColumn = useMemo(
        () => ({
            meta: {
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const handleOpenModal = async (type: 'add' | 'update') => {
        if (type === 'add' && internationalCompanies?.data?.length) {
            const currentICPData = await getInternationalCompanyGroupData({
                internationalCompanyId: internationalCompanies.data[0].id,
            });
            setModalData({
                ...emptyModalData,
                international_company: {
                    id: internationalCompanies.data[0].id,
                    name: internationalCompanies.data[0].name,
                },
                available_company_groups: currentICPData.data?.data?.companyGroups || [],
                available_users: currentICPData.data?.data?.users || [],
            });
        }
        setModalType(type);
        setIsModalOpen(true);
    };

    const updateModalData = async (internationalCompany: { id: number; name: string }) => {
        const currentICPData = await getInternationalCompanyGroupData({
            internationalCompanyId: internationalCompany.id,
        });
        if (internationalCompany.id === internationalCompanyGroupData?.data.international_company.id)
            setModalData((prev) => ({
                ...(internationalCompanyGroupData.data as IOptionalInternationalCompanyGroup),
                globalCompanyId: globalCompanyId as number,
                name: prev.name,
            }));
        else
            setModalData((prev) => ({
                ...prev,
                international_company: {
                    id: internationalCompany.id,
                    name: internationalCompany.name,
                },
                available_company_groups: currentICPData.data?.data?.companyGroups || [],
                available_users: currentICPData.data?.data?.users || [],
                users: [],
                company_groups: [],
            }));
    };

    const columns: ColumnDef<IInternationalCompanyGroup, any>[] = useMemo(() => {
        const tableColumns: (ColumnDef<IInternationalCompanyGroup, any> | null)[] = [
            {
                header: () => translate('icg.column.icp') as string,
                accessorKey: 'name',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as IInternationalCompanyGroup;
                    return (
                        <Box
                            onClick={async () => {
                                const internationalCompanyGroupData = await getInternationalCompanyGroup({
                                    globalCompanyId: globalCompanyId as number,
                                    internationalCompanyGroupId: cellValue.id,
                                });
                                if (internationalCompanyGroupData.data)
                                    setModalData({
                                        ...(internationalCompanyGroupData.data.data as IOptionalInternationalCompanyGroup),
                                        globalCompanyId: globalCompanyId as number,
                                        internationalCompanyGroupId: cellValue.id,
                                    });
                                handleOpenModal('update');
                            }}
                        >
                            <Text {...linkStyle}>{cellValue.name}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<IInternationalCompanyGroup, any>,

            {
                header: () => translate('icg.column.international_cluster') as string,
                accessorKey: 'international_company.name',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as IInternationalCompanyGroup;
                    return (
                        <Box display="flex">
                            <Text>{cellValue.international_company.name}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<IInternationalCompanyGroup, any>,

            {
                header: () => translate('icg.column.number_of_users') as string,
                accessorKey: 'number_of_users',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as IInternationalCompanyGroup;
                    return (
                        <Box display="flex">
                            <Text>{cellValue.number_of_users}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<IInternationalCompanyGroup, any>,
        ];

        return tableColumns.filter((e): e is ColumnDef<IInternationalCompanyGroup, any> => e !== null);
    }, [internationalCompanyGroupsData, languages, userInfo]);

    const table = useReactTable<IInternationalCompanyGroup>({
        columns,
        data: data as IInternationalCompanyGroup[],
        defaultColumn,
        sortingFns: sortTypes,
        initialState: { columnFilters: filters },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        setFilters(table.getState().columnFilters);
    }, [table.getState().columnFilters]);

    const props = useMemo(
        () => ({
            languages,
            filters,
            retailers,
            isRetailersLoading,
            isRetailerError,
            columns,
            table,
            internationalCompanyGroupsData,
            isError,
            isLoading,
            companyInfo,
            userInfo,
            defaultColumn,
            data,
            sortTypes,
            modalType,
            isModalOpen,
            modalData,
            setModalData,
            setLanguages,
            caseInsensitiveSort,
            handleOpenModal,
            setModalType,
            setIsModalOpen,
            updateModalData,
            isDeleteOpen,
            onDeleteOpen,
            onDeleteClose,
            internationalCompanyGroupData,
            internationalCompanies,
            isInternationalCompaniesError,
            isInternationalCompaniesLoading,
            isInternationalCompanyGroupLoading,
            getInternationalCompanyGroup,
            globalCompanyId,
            emptyModalData,
        }),
        [
            languages,
            filters,
            retailers,
            isRetailersLoading,
            isRetailerError,
            columns,
            table,
            companyInfo,
            userInfo,
            defaultColumn,
            data,
            internationalCompanies,
            sortTypes,
            modalType,
            isModalOpen,
            modalData,
            isDeleteOpen,
        ],
    );

    return props;
};

export type TInternationalCompanyGroup = ReturnType<typeof useInternationalCompanyGroup>;

export default useInternationalCompanyGroup;
